import {
  PlusIcon,
  TrashIcon,
  PencilIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import { useStore } from "../contexts/StoreContext";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchCampaigns, fetchPasses } from "../api/queries";
import { useState } from "react";
import {
  createCampaign,
  updateCampaign,
  deleteCampaign,
} from "../api/mutations";
import { GiftIcon } from "@heroicons/react/24/outline";
import { useLanguage } from "../contexts/LanguageContext";
import EmptyState from "../components/EmptyState";
import DeleteAlert from "../components/DeleteAlert";
import TrialEndedAlert from "../components/TrialEndedAlert";
import { getAmountTextFromType } from "../utils/passUtils";
import Header from "../components/Header";
import Button from "../components/Button";
import Drawer from "../blocks/drawer";

function Tooltip({ text }) {
  return (
    <div className="relative group">
      <InformationCircleIcon className="h-5 w-5 text-gray-400" />
      <div className="absolute z-10 hidden group-hover:block w-64 p-2 text-sm text-white bg-gray-900 rounded-lg shadow-lg top-full left-0 mt-1">
        {text}
      </div>
    </div>
  );
}

export default function Campaigns() {
  const { t } = useLanguage();
  const { selectedStore, canManageStore } = useStore();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    kind: "purchase",
    pass_id: "",
    point: 1,
    amount_type: "fixed",
    started_at: "",
    ended_at: "",
    target_pass_id: "",
    currencyMultiplier: 100,
  });
  const [selectedPassId, setSelectedPassId] = useState("all");
  const [errors, setErrors] = useState([]);
  const [isDeleteAlertOpen, setIsDeleteAlertOpen] = useState(false);
  const [deleteCampaignId, setDeleteCampaignId] = useState(null);
  const [isTrialEndedAlertOpen, setIsTrialEndedAlertOpen] = useState(false);

  const { data: campaigns, isLoading } = useQuery({
    queryKey: ["campaigns", selectedStore?.id],
    queryFn: () => fetchCampaigns(selectedStore?.id),
    enabled: !!selectedStore?.id,
  });

  const { data: passes } = useQuery({
    queryKey: ["passes", selectedStore?.id],
    queryFn: () => fetchPasses(selectedStore?.id),
    enabled: !!selectedStore?.id,
  });

  const createCampaignMutation = useMutation({
    mutationFn: (data) =>
      createCampaign({ storeId: selectedStore.id, campaignData: data }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["campaigns", selectedStore?.id],
      });
      handleCloseModal();
    },
    onError: (error) => {
      setErrors(error.response.data.errors);
    },
  });

  const updateCampaignMutation = useMutation({
    mutationFn: (data) =>
      updateCampaign({
        storeId: selectedStore.id,
        campaignId: editingCampaign.id,
        campaignData: data,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["campaigns", selectedStore?.id],
      });
      handleCloseModal();
    },
    onError: (error) => {
      setErrors(error.response.data.errors);
    },
  });

  const deleteCampaignMutation = useMutation({
    mutationFn: (campaignId) =>
      deleteCampaign({
        storeId: selectedStore.id,
        campaignId,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["campaigns", selectedStore?.id],
      });
    },
  });

  const handleOpenModal = (campaign = null) => {
    setErrors([]);
    // Check if store is in trial period (15 days from creation)
    const isInTrialPeriod = () => {
      if (!selectedStore?.trial_expires_at) return false;
      const trialEndDate = new Date(selectedStore.trial_expires_at);
      trialEndDate.setDate(trialEndDate.getDate());
      return new Date() < trialEndDate;
    };

    // Check subscription limits
    if (
      selectedStore?.subscription_status !== "active" &&
      selectedStore?.subscription_status !== "trialing" &&
      selectedStore?.subscription_status !== "past_due" &&
      !isInTrialPeriod()
    ) {
      setIsTrialEndedAlertOpen(true);
      //alert('Your trial period has ended. Please subscribe to a plan to create pass layouts.');
      return;
    }

    if (campaign) {
      setEditingCampaign(campaign);
      setFormData({
        name: campaign.name || "",
        description: campaign.description || "",
        kind: campaign.kind || "purchase",
        pass_id: campaign.pass.id ? campaign.pass.id.toString() : "",
        point: campaign.point || 1,
        amount_type: campaign.amount_type || "fixed",
        started_at: campaign.started_at || "",
        ended_at: campaign.ended_at || "",
        target_pass_id: campaign.target_pass_id || "",
        currencyMultiplier: campaign.currencyMultiplier || 100,
        pass: campaign.pass || null,
      });
    } else {
      setEditingCampaign(null);
      setFormData({
        name: "",
        description: "",
        kind: "purchase",
        pass_id: "",
        point: 1,
        amount_type: "fixed",
        started_at: "",
        ended_at: "",
        target_pass_id: "",
        currencyMultiplier: 100,
        pass: null,
      });
    }
    setIsOpen(true);
  };

  const handleCloseModal = () => {
    setIsOpen(false);
    setEditingCampaign(null);
    setFormData({
      name: "",
      description: "",
      kind: "purchase",
      pass_id: "",
      point: 1,
      amount_type: "fixed",
      started_at: "",
      ended_at: "",
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (editingCampaign) {
      updateCampaignMutation.mutate(formData);
    } else {
      createCampaignMutation.mutate(formData);
    }
  };

  const campaignTypes = ["purchase", "redemption"];

  const groupCampaignsByKind = (campaigns) => {
    // Initialize with all campaign types
    const grouped = campaignTypes.reduce((acc, type) => {
      acc[type] = [];
      return acc;
    }, {});

    // Add existing campaigns
    campaigns?.forEach((campaign) => {
      grouped[campaign.kind].push(campaign);
    });

    return grouped;
  };

  const campaignDescriptions = {
    purchase: t("campaigns.descriptions.purchase"),
    redemption: t("campaigns.descriptions.redemption"),
    upgrade: t("campaigns.descriptions.upgrade"),
  };

  const filteredCampaigns = campaigns?.filter(
    (campaign) =>
      campaign.pass.pass.type !== "coupon" &&
      (selectedPassId === "all" ||
        campaign.pass.id.toString() === selectedPassId),
  );

  const groupedCampaigns = groupCampaignsByKind(filteredCampaigns);

  const modalTitle = editingCampaign
    ? t("campaigns.form.edit.title")
    : t("campaigns.form.create.title");
  const submitButtonText = editingCampaign
    ? t("campaigns.form.edit.button")
    : t("campaigns.form.create.button");

  if (isLoading) {
    return (
      <div className="flex items-center justify-center">
        <div className="text-gray-500">{t("campaigns.loading")}</div>
      </div>
    );
  }

  return (
    <>
      <div className="max-w-8xl mx-auto space-y-6 lg:space-y-8">
        <Header
          icon={GiftIcon}
          title={t("campaigns.title")}
          description={t("campaigns.description")}
          rightSection={
            <div className="flex items-center space-x-4">
              <select
                value={selectedPassId}
                onChange={(e) => setSelectedPassId(e.target.value)}
                className="rounded-md border-gray-300 shadow-xs focus:border-indigo-500 focus:ring-indigo-500"
              >
                <option value="all">{t("campaigns.filter.allPasses")}</option>
                {passes
                  ?.filter((pass) => pass.pass.type !== "coupon")
                  .map((pass) => (
                    <option key={pass.id} value={pass.id}>
                      {pass.pass.name}
                    </option>
                  ))}
              </select>
              {canManageStore && (
                <Button onClick={() => handleOpenModal()} icon={PlusIcon}>
                  {t("campaigns.new")}
                </Button>
              )}
            </div>
          }
        />
        {campaignTypes.map((kind) => (
          <div key={kind}>
            <h2 className="text-lg font-medium text-gray-900">
              {t(`campaigns.form.fields.types.${kind}`)} {t("campaigns.title")}
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              {campaignDescriptions[kind]}
            </p>
            <div className="mt-4 overflow-x-auto">
              {groupedCampaigns[kind]?.length > 0 ? (
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 w-1/3"
                      >
                        {kind === "upgrade"
                          ? t("campaigns.table.targetPass")
                          : t("campaigns.table.passType")}
                      </th>
                      <th
                        scope="col"
                        className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-3 w-1/3"
                      >
                        {kind === "upgrade"
                          ? t("campaigns.table.fromPass")
                          : t("campaigns.table.name")}
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 w-1/6"
                      >
                        {kind === "upgrade"
                          ? t("campaigns.table.targetAmount")
                          : t("campaigns.table.amount")}
                      </th>
                      <th
                        scope="col"
                        className="relative py-3.5 pl-3 pr-4 sm:pr-3 w-1/6"
                      >
                        <span className="sr-only">
                          {t("campaigns.table.edit")}
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {groupedCampaigns[kind].map((campaign) => (
                      <tr key={campaign.id}>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {campaign.kind === "upgrade"
                            ? `${campaign.target_pass.pass.name}`
                            : `${campaign.pass.pass.name}`}
                        </td>
                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-500 sm:pl-3">
                          {campaign.kind === "upgrade"
                            ? `${campaign.pass.pass.name}`
                            : campaign.name}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {campaign.pass.pass.type === "discount"
                            ? `Discount ${campaign.pass.pass.discount}%`
                            : campaign.amount_type === "multiplier"
                              ? campaign.point < 1
                                ? `%${campaign.point * 100}`
                                : `X ${campaign.point}`
                              : `${getAmountTextFromType(
                                  campaign.pass.pass.type,
                                  selectedStore?.currency,
                                )} ${campaign.point}`}
                        </td>
                        <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-3">
                          <div
                            className={`flex justify-end space-x-3 ${canManageStore ? "" : "hidden"}`}
                          >
                            <button
                              onClick={() => handleOpenModal(campaign)}
                              className={`text-indigo-600
                                ${campaign.pass.pass.type === "discount" || campaign.pass.pass.type === "membership" ? "hidden" : ""}`}
                            >
                              <PencilIcon className="h-5 w-5" />
                              <span className="sr-only">, {campaign.name}</span>
                            </button>
                            <button
                              onClick={() => {
                                setIsDeleteAlertOpen(true);
                                setDeleteCampaignId(campaign.id);
                              }}
                              className={`text-red-600 
                                ${campaign.pass.pass.type === "stamp" && campaign.kind === "purchase" ? "hidden" : ""}
                                ${campaign.pass.pass.type === "discount" || campaign.pass.pass.type === "membership" ? "hidden" : ""}`}
                            >
                              <TrashIcon
                                className="h-5 w-5"
                                aria-hidden="true"
                              />
                              <span className="sr-only">
                                {t("campaigns.table.delete")} {campaign.name}
                              </span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <EmptyState
                  title={t("campaigns.empty", {
                    type: t(`campaigns.form.fields.types.${kind}`),
                  })}
                  description={campaignDescriptions[kind]}
                  className="bg-white"
                  icon={GiftIcon}
                />
              )}
            </div>
          </div>
        ))}

        <Drawer
          open={isOpen}
          setOpen={setIsOpen}
          header={modalTitle}
          onSubmit={handleSubmit}
          onCancel={() => setIsOpen(false)}
          submitText={t("common.save")}
          cancelText={t("common.cancel")}
          isLoading={updateCampaignMutation.isPending}
          submitDisabled={updateCampaignMutation.isPending}
        >
          <div className="px-4 py-6 sm:px-6 space-y-4">
            <div>
              <div>
                {errors.length > 0 && (
                  <div className="rounded-md bg-red-50 p-4 mb-4">
                    <div className="flex">
                      <div className="shrink-0">
                        <svg
                          className="h-5 w-5 text-red-400"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-red-800">
                          {errors.map((error, index) => (
                            <p key={index}>{error}</p>
                          ))}
                        </h3>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className={`${editingCampaign ? "hidden" : ""}`}>
                <label className="block text-sm font-medium text-gray-700">
                  {t("campaigns.form.fields.selectPass")}
                </label>
                <select
                  value={formData.pass_id}
                  onChange={(e) => {
                    const selectedPassId = e.target.value;
                    const selectedPass = passes?.find(
                      (pass) => pass.id.toString() === selectedPassId,
                    );
                    let defaultPoint = 100;

                    if (selectedPass?.pass?.type === "stamp") {
                      defaultPoint =
                        formData.kind === "purchase"
                          ? 1
                          : selectedPass.pass.stamp_count;
                    }

                    // Re-selecting the placeholder option leaves no match, so
                    // every read below has to tolerate a missing pass.
                    const isCoupon = selectedPass?.pass?.type === "coupon";

                    setFormData({
                      ...formData,
                      pass_id: selectedPassId,
                      pass: selectedPass,
                      amount_type: "fixed",
                      point: isCoupon ? 1 : defaultPoint,
                      kind: isCoupon ? "upgrade" : "purchase",
                    });
                  }}
                  className="w-full rounded-md border-gray-300 shadow-xs focus:border-indigo-500 focus:ring-indigo-500"
                  required
                >
                  <option value="">
                    {t("campaigns.form.fields.selectPassPlaceholder")}
                  </option>
                  {passes
                    ?.filter(
                      (pass) =>
                        pass.pass.type !== "coupon" &&
                        pass.pass.type !== "membership",
                    )
                    .map((pass) => (
                      <option key={pass.id} value={pass.id}>
                        {pass.pass.name}
                      </option>
                    ))}
                </select>
              </div>
            </div>
            <div className={formData.pass_id ? "space-y-4" : "hidden"}>
              <div className={`${editingCampaign ? "hidden" : ""}`}>
                <label className="block text-sm font-medium text-gray-700">
                  {t("campaigns.form.fields.campaignType")}
                </label>
                <select
                  value={formData.kind}
                  onChange={(e) => {
                    const selectedPassId = formData.pass_id;
                    const selectedPass = passes?.find(
                      (pass) => pass.id.toString() === selectedPassId,
                    );
                    let defaultPoint = 100;

                    if (selectedPass?.pass?.type === "stamp") {
                      defaultPoint =
                        e.target.value === "purchase"
                          ? 1
                          : selectedPass.pass.stamp_count;
                    }

                    setFormData({
                      ...formData,
                      kind: e.target.value,
                      point: defaultPoint,
                    });
                  }}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-xs focus:border-indigo-500 focus:ring-indigo-500"
                  required
                >
                  {formData.pass?.pass?.type !== "coupon" && (
                    <>
                      <option value="purchase">
                        {t("campaigns.form.fields.types.purchase")}
                      </option>
                      <option value="redemption">
                        {t("campaigns.form.fields.types.redemption")}
                      </option>
                    </>
                  )}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  {t("campaigns.form.fields.name")}
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      name: e.target.value,
                    })
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-xs focus:border-indigo-500 focus:ring-indigo-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  {t("campaigns.form.fields.description")}
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      description: e.target.value,
                    })
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-xs focus:border-indigo-500 focus:ring-indigo-500"
                  required
                />
              </div>
              {formData.pass?.pass?.type !== "stamp" &&
                passes?.find((p) => p.id.toString() === formData.pass_id)?.pass
                  ?.type !== "stamp" && (
                  <div>
                    <div className="flex items-center space-x-2 mb-2">
                      <label className="block text-sm font-medium text-gray-700">
                        {t("campaigns.form.fields.amountType.label")}
                      </label>
                      <Tooltip
                        text={t("campaigns.form.fields.amountType.tooltip")}
                      />
                    </div>
                    <div className="mb-3">
                      <div className="flex items-center space-x-6">
                        <div className="flex items-center">
                          <input
                            type="radio"
                            id="fixedAmount"
                            name="amount_type"
                            checked={formData.amount_type === "fixed"}
                            onChange={() =>
                              setFormData({
                                ...formData,
                                amount_type: "fixed",
                              })
                            }
                            className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          <label
                            htmlFor="fixedAmount"
                            className="ml-2 block text-sm text-gray-900"
                          >
                            {t("campaigns.form.fields.amountType.fixed")}
                          </label>
                        </div>
                        <div className="flex items-center">
                          <input
                            type="radio"
                            id="customAmount"
                            name="amount_type"
                            checked={formData.amount_type === "custom"}
                            onChange={() =>
                              setFormData({
                                ...formData,
                                amount_type: "custom",
                              })
                            }
                            className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          <label
                            htmlFor="customAmount"
                            className="ml-2 block text-sm text-gray-900"
                          >
                            {t("campaigns.form.fields.amountType.custom")}
                          </label>
                        </div>

                        {formData.kind === "purchase" &&
                          (formData.pass?.pass?.type === "cashback" ||
                            passes?.find(
                              (p) => p.id.toString() === formData.pass_id,
                            )?.pass?.type === "cashback" ||
                            formData.pass?.pass?.type === "cashback_money" ||
                            passes?.find(
                              (p) => p.id.toString() === formData.pass_id,
                            )?.pass?.type === "cashback_money") && (
                            <div className="flex items-center">
                              <input
                                type="radio"
                                id="multiplierAmount"
                                name="amount_type"
                                checked={formData.amount_type === "multiplier"}
                                onChange={() =>
                                  setFormData({
                                    ...formData,
                                    amount_type: "multiplier",
                                  })
                                }
                                className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-500"
                              />
                              <label
                                htmlFor="multiplierAmount"
                                className="ml-2 block text-sm text-gray-900"
                              >
                                {t(
                                  "campaigns.form.fields.amountType.multiplier",
                                )}
                              </label>
                            </div>
                          )}
                      </div>
                    </div>
                  </div>
                )}
              <div>
                <div className="flex items-center space-x-2 mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    {formData.amount_type === "custom"
                      ? t("campaigns.form.fields.amount.custom")
                      : formData.amount_type === "multiplier"
                        ? formData.pass?.pass?.type === "cashback_money"
                          ? t("campaigns.form.fields.amount.multiplierMoney")
                          : t("campaigns.form.fields.amount.multiplier")
                        : t("campaigns.form.fields.amount.fixed")}
                  </label>
                </div>
                <div className="mt-1 flex rounded-md shadow-xs">
                  <input
                    type="number"
                    value={formData.point}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        point: parseFloat(e.target.value),
                      })
                    }
                    className="block w-full rounded-none rounded-l-md border-gray-300 shadow-xs focus:border-indigo-500 focus:ring-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    required
                  />
                  <span className="inline-flex items-center rounded-r-md border border-l-0 border-gray-300 bg-gray-50 px-3 text-gray-500 sm:text-sm">
                    {getAmountTextFromType(
                      formData.pass?.pass?.type,
                      selectedStore?.currency,
                      formData.amount_type,
                    )}
                  </span>
                </div>
                <span
                  className="text-xs text-gray-500"
                  hidden={formData.amount_type !== "multiplier"}
                >
                  {formData.pass?.pass?.type === "cashback_money" ? (
                    <span
                      className="text-xs text-gray-500"
                      hidden={formData.amount_type !== "multiplier"}
                    >
                      {t("campaigns.form.fields.amount.multiplierHintMoney", {
                        amount: 20,
                        points: 20 * formData.point,
                        currency: selectedStore?.currency,
                      })}
                    </span>
                  ) : (
                    <span
                      className="text-xs text-gray-500"
                      hidden={formData.amount_type !== "multiplier"}
                    >
                      {t("campaigns.form.fields.amount.multiplierHint", {
                        amount: 20,
                        points: 20 * formData.point,
                        currency: selectedStore?.currency,
                      })}
                    </span>
                  )}
                </span>
              </div>
            </div>
          </div>
        </Drawer>
      </div>

      <DeleteAlert
        isOpen={isDeleteAlertOpen}
        onClose={() => setIsDeleteAlertOpen(false)}
        onConfirm={() => {
          deleteCampaignMutation.mutate(deleteCampaignId);
          setIsDeleteAlertOpen(false);
        }}
      />
      <TrialEndedAlert
        isOpen={isTrialEndedAlertOpen}
        onClose={() => setIsTrialEndedAlertOpen(false)}
        subscriptionStatus={selectedStore?.subscription_status}
      />
    </>
  );
}
